'use client'

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ExpAnimationData {
  oldExp: number;
  newExp: number;
  oldLevel: number;
  newLevel: number;
  oldPercentage: number;
  newPercentage: number;
  leveledUp: boolean;
  expGained: number;
}

interface ExpContextType {
  triggerExpAnimation: (data: ExpAnimationData) => void;
  isAnimating: boolean;
  animationData: ExpAnimationData | null;
  clearAnimation: () => void;
}

const ExpContext = createContext<ExpContextType | undefined>(undefined);

export const useExpAnimation = () => {
  const context = useContext(ExpContext);
  if (context === undefined) {
    throw new Error('useExpAnimation must be used within an ExpProvider');
  }
  return context;
};

interface ExpProviderProps {
  children: ReactNode;
}

export const ExpProvider: React.FC<ExpProviderProps> = ({ children }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationData, setAnimationData] = useState<ExpAnimationData | null>(null);

  const triggerExpAnimation = useCallback((data: ExpAnimationData) => {
    setAnimationData(data);
    setIsAnimating(true);
    
    // Auto-clear animation after 3 seconds
    setTimeout(() => {
      setIsAnimating(false);
      setAnimationData(null);
    }, 3000);
  }, []);

  const clearAnimation = useCallback(() => {
    setIsAnimating(false);
    setAnimationData(null);
  }, []);

  return (
    <ExpContext.Provider value={{
      triggerExpAnimation,
      isAnimating,
      animationData,
      clearAnimation,
    }}>
      {children}
    </ExpContext.Provider>
  );
};
